import { type NPVSettings } from "@/types/types";
import { calculateAge } from "../UP/up-utils";
import type { Clients, NPVRow, UPRow } from "@/types/types";

export type NpvHelpers = {
  InvestmentWithdrawals: number;
  TotalIncome: number;
  TotalSpending: number;
};

export type NPV = {
  age: number;
  npv: number;
};

export class NetPresentValue {
  static calcHelpers(settings: NPVSettings, grid: NPVRow[], age1: number, age2?: number) {
    const newHelpers: Array<NpvHelpers> = grid.map((item) => {
      return {
        TotalIncome: this.calcTotalIncome(item),
        TotalSpending: this.calcTotalSpending(item),
        InvestmentWithdrawals: this.calcTotalIncome(item) - this.calcTotalSpending(item),
      };
    });

    const maxAgePrimary = age1 >= 90 ? 110 : 100;
    const maxAgeSecondary = age2 ? (age2 >= 90 ? 110 : 100) : maxAgePrimary;

    const minAge = Math.min(maxAgePrimary, maxAgeSecondary);
    const minClientsAge = age2 ? Math.min(age1, age2) : age1;

    const indexShift =
      grid.length -
      grid.filter((i: any) => {
        if (age2 && age2 > age1) {
          return i.Age2.value >= age2;
        } else {
          return i.Age.value >= age1 || !i.Age.value;
        }
      }).length +
      settings.yearsToRetire;

    const options = Array.from({ length: minAge - minClientsAge + 1 }, (_, index) => 5 + index);

    const npv: NPV[] = options.map((age) => {
      return {
        age,
        npv: getNPV(settings.irr, 0, getRangeValues(age, indexShift, newHelpers)),
      };
    });

    return {
      newHelpers,
      npv,
    };
  }

  static calcTotalIncome(item: NPVRow) {
    return (
      item.SocialSecurity.value +
      item.Pension.value +
      (item.Income.isExcluded ? 0 : item.Income.value) +
      (item.Income2.isExcluded ? 0 : item.Income2.value)
    );
  }

  static calcTotalSpending(item: NPVRow) {
    return (
      (item.Spending.isExcluded ? 0 : item.Spending.value) +
      (item.Spending2.isExcluded ? 0 : item.Spending2.value) +
      (item.Spending3.isExcluded ? 0 : item.Spending3.value)
    );
  }

  static createNpv(
    grid: NPVRow[],
    helpers: NpvHelpers[],
    npv: NPV[],
    settings: NPVSettings,
    household: Clients,
    uqGrid?: UPRow[]
  ): NPVRow[] {
    const npvVal = npv.find((item) => item?.age === settings?.npvAge)?.npv;
    const newGrid: NPVRow[] = [];

    if (!household.primaryContact) return [];

    const age = calculateAge(household.primaryContact.birthDate.toString(), 0).value;
    const newStartingAge = age + settings.yearsToRetire;
    const newEndingAge = newStartingAge + settings.npvAge;

    grid.forEach((item, i) => {
      const isInStartingAge = newStartingAge <= item.Age.value;
      const isNpvAgeInRange = isInStartingAge && newEndingAge > item.Age.value;
      const npv =
        item?.Age.value === newStartingAge ? npvVal : i === 0 ? 0 : newGrid[i - 1]?.YearEnd;

      const YearEnd = Math.max(
        0,
        npv * (1 + settings.irr) +
          item.SocialSecurity.value +
          item.Pension.value +
          (item.Income.isExcluded ? 0 : item.Income.value) +
          (item.Income2.isExcluded ? 0 : item.Income2.value) -
          (item.Spending.isExcluded ? 0 : item.Spending.value) -
          (item.Spending2.isExcluded ? 0 : item.Spending2.value) -
          (item.Spending3.isExcluded ? 0 : item.Spending3.value)
      );

      const isYearEndNeeded = item.Age.value > newEndingAge;
      const isYearEndValid = isInStartingAge && !isYearEndNeeded;

      newGrid.push({
        ...item,
        InvestmentWithdrawals: {
          defaultValue: 0,
          manualChange: false,
          value:
            isNpvAgeInRange && Array.isArray(helpers) && i >= 0 && i < helpers.length
              ? (helpers[i]?.InvestmentWithdrawals ?? 0)
              : 0,
          note: "",
        },
        NPVSolution: {
          defaultValue: 0,
          manualChange: false,
          value: isInStartingAge && (npv ?? 0) > 0 && !isYearEndNeeded ? (npv ?? 0) : 0,
          note: "",
        },
        YearEnd: isYearEndValid
          ? YearEnd
          : isYearEndNeeded
            ? 0
            : Array.isArray(uqGrid) &&
                i >= 0 &&
                i < uqGrid.length &&
                uqGrid[i]?.YearEnd !== undefined
              ? uqGrid[i].YearEnd
              : 0,
      });
    });

    return newGrid;
  }
}

function getRangeValues(year: number, indexShift: number, data: Array<NpvHelpers>) {
  const arr: number[] = [];

  for (let index = indexShift; index < indexShift + year; index++) {
    const item = data?.[index];
    if (item && item.InvestmentWithdrawals !== undefined) {
      arr.push(item.InvestmentWithdrawals);
    }
  }

  return arr;
}

function getNPV(rate: number, initialCost: number, cashFlows: number[]) {
  let npv = initialCost;

  for (let i = 0; i < cashFlows.length; i++) {
    npv += cashFlows[i] / Math.pow(rate + 1, i + 1);
  }

  return npv * -1;
}

export function getInvestmentWithdrawalsSum(npvGrid: NPVRow[]) {
  const sum =
    npvGrid?.reduce((prev, curr) => {
      const val = curr?.InvestmentWithdrawals?.value;
      return prev + (typeof val === "number" ? val : 0);
    }, 0) ?? 0;

  return {
    value: Math.abs(sum),
    isNeg: sum > 0 ? false : true,
  };
}
